"use client";
import { getApiBaseUrl } from "@/lib/apiBase";
import { fetchJsonArray } from "@/lib/fetchJsonSafe";

import { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
const BASE_URL = getApiBaseUrl();

/** Нүүр hero — гарчгийн дээрх товч тайлбар */
const BANNER_KICKER =
  "Бодит хэрэглэгчийн сэтгэгдэл, шударга үнэлгээгээр сонголтоо хөнгөвчилнө.";

/** API-д баннер байхгүй үед нүүр хоосон харагдахгүйн тулд */
const STATIC_HERO = {
  title: "Chanaraas",
  subtitle: "Үйлчилгээний тойм, үнэлгээ — итгэлтэй сонголт.",
  ctaHref: "/reviews",
  ctaLabel: "Тойм үзэх",
};

/** DB-д бүрэн URL эсвэл /uploads/... гэж ирж болно */
function bannerImageSrc(imageUrl) {
  if (!imageUrl || typeof imageUrl !== "string") return "";
  const t = imageUrl.trim();
  if (/^data:/i.test(t)) return t;
  if (/^https?:\/\//i.test(t)) return t;
  if (!BASE_URL) return t.startsWith("/") ? t : `/${t}`;
  return `${BASE_URL}${t.startsWith("/") ? "" : "/"}${t}`;
}

function getBannerDescription(banner) {
  const raw =
    banner?.banner_description || banner?.description || banner?.subtitle;
  if (raw == null || typeof raw !== "string") return "";
  return raw.trim();
}

function hasUsableBannerLink(link) {
  if (link == null || typeof link !== "string") return false;
  const t = link.trim();
  return t !== "" && t !== "#";
}

/** NEXT_PUBLIC_SITE_URL (жишээ https://chanaraas.mn) — бүтэн URL-ийг дотоод path болгон таньна */
function getPublicSiteOrigin() {
  const raw = process.env.NEXT_PUBLIC_SITE_URL;
  if (!raw || typeof raw !== "string") return null;
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
}

/**
 * Баннерын линк → дотоод шилжих эсвэл гадаад нээх.
 * Слайдер дотор `<Link>` заримдаа товчлуур буруу ажилладаг тул `router.push` ашиглана.
 */
function resolveBannerLinkAction(hrefTrim) {
  const t = hrefTrim.trim();
  if (!t || t === "#") return { kind: "none" };
  if (t.startsWith("//")) {
    return { kind: "external", url: `https:${t}` };
  }
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      const envOrigin = getPublicSiteOrigin();
      if (envOrigin && u.origin === envOrigin) {
        const path = `${u.pathname}${u.search}${u.hash}` || "/";
        return { kind: "internal", path };
      }
      if (typeof window !== "undefined" && u.origin === window.location.origin) {
        const path = `${u.pathname}${u.search}${u.hash}` || "/";
        return { kind: "internal", path };
      }
      return { kind: "external", url: t };
    } catch {
      return { kind: "external", url: t };
    }
  }
  const path = t.startsWith("/") ? t : `/${t.replace(/^\/+/, "")}`;
  return { kind: "internal", path };
}

const AUTOPLAY_MS = 5200;
const HERO_SECTION_CLASS =
  "relative w-full overflow-hidden min-h-[min(92vw,420px)] h-[clamp(420px,58vh,760px)] md:min-h-[480px] md:h-[clamp(480px,62vh,780px)]";

export default function BannerSlider() {
  const router = useRouter();
  const [bannersHomeBig, setBannersHomeBig] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);
  const progressIntervalRef = useRef(null);

  const settings = {
    dots: false,
    infinite: true,
    speed: 900,
    cssEase: "cubic-bezier(0.33, 1, 0.68, 1)",
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: AUTOPLAY_MS,
    arrows: false,
    beforeChange: (current, next) => {
      setCurrentSlide(next);
      setProgress(0);
    },
  };

  const pauseAutoplay = () => sliderRef.current?.slickPause?.();
  const resumeAutoplay = () => sliderRef.current?.slickPlay?.();

  const safeBannersHomeBig = Array.isArray(bannersHomeBig) ? bannersHomeBig : [];

  useEffect(() => {
    const fetchBannersHomebig = async () => {
      try {
        setLoading(true);
        const data = await fetchJsonArray(`/api/banner?type=banner&bannertypeid=1`);
        setBannersHomeBig(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching banners:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBannersHomebig();
  }, []);

  // Progress bar animation
  useEffect(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          return 0;
        }
        return prev + (100 / (AUTOPLAY_MS / 50));
      });
    }, 50);

    progressIntervalRef.current = interval;

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [currentSlide]);

  const goToPrev = () => {
    sliderRef.current?.slickPrev();
  };

  const goToNext = () => {
    sliderRef.current?.slickNext();
  };

  if (loading) {
    return (
      <div className="mx-auto flex flex-col md:flex-row">
        <div
          className={`${HERO_SECTION_CLASS} banner-skeleton-shimmer bg-zinc-200 dark:bg-zinc-800`}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-sm font-medium tracking-wide text-zinc-500 dark:text-zinc-400">
              Ачааллаж байна...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (safeBannersHomeBig.length === 0) {
    return (
      <div className="mx-auto flex flex-col md:flex-row">
        <div className={`${HERO_SECTION_CLASS} bg-gradient-to-br from-slate-950 via-[#2c3d56] to-slate-900`}>
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
            aria-hidden
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-transparent" />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.35]"
            style={{
              background:
                "radial-gradient(ellipse 85% 65% at 20% 45%, rgba(87,112,153,0.22), transparent 55%), radial-gradient(ellipse 70% 50% at 100% 100%, rgba(0,0,0,0.45), transparent 50%)",
            }}
            aria-hidden
          />
          <div className="relative z-10 flex h-full flex-col items-start justify-center px-5 py-12 sm:px-8 md:px-14 lg:px-20">
            <div className="banner-text-panel max-w-xl rounded-3xl border border-white/12 bg-white/[0.07] p-6 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.55)] ring-1 ring-inset ring-white/10 backdrop-blur-xl sm:max-w-2xl sm:p-8 md:max-w-2xl">
              <div
                className="mb-5 h-1 w-14 rounded-full bg-gradient-to-r from-[#8fa8cc] to-[#577099]"
                aria-hidden
              />
              <p className="mb-3 max-w-xl text-pretty text-xs font-medium uppercase tracking-[0.12em] text-white/65 md:text-sm md:normal-case md:tracking-normal md:text-white/70">
                {BANNER_KICKER}
              </p>
              <h2 className="text-balance text-3xl font-semibold tracking-tight text-white [text-shadow:0_2px_28px_rgba(0,0,0,0.45)] md:text-5xl md:leading-[1.06]">
                {STATIC_HERO.title}
              </h2>
              <p className="mt-4 max-w-xl text-pretty text-sm leading-relaxed text-white/[0.88] md:text-base">
                {STATIC_HERO.subtitle}
              </p>
              <Link
                href={STATIC_HERO.ctaHref}
                className="mt-8 inline-flex items-center justify-center rounded-full bg-white px-7 py-3 text-sm font-semibold text-slate-900 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.35)] ring-1 ring-white/40 transition hover:bg-white/95 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
              >
                {STATIC_HERO.ctaLabel}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex flex-col md:flex-row">
      {/* ✅ Том Slider */}
      <div
        className={HERO_SECTION_CLASS}
        onMouseEnter={pauseAutoplay}
        onMouseLeave={resumeAutoplay}
        onFocusCapture={pauseAutoplay}
        onBlurCapture={resumeAutoplay}
      >
        <Slider {...settings} ref={sliderRef} className="banner-slider-root h-full">
          {safeBannersHomeBig.map((banner, index) => {
            const bannerDesc = getBannerDescription(banner);
            const href = banner.banner_link;
            const showLink = hasUsableBannerLink(href);
            const hrefTrim = showLink ? String(href).trim() : "";
            const linkAction = showLink ? resolveBannerLinkAction(hrefTrim) : { kind: "none" };
            const isActive = index === currentSlide;

            return (
            <div
              key={banner.id}
              className={`relative h-full min-h-[inherit] w-full overflow-hidden ${isActive ? "banner-slide--active" : ""}`}
            >
              {/* next/image биш: partner/admin-аас ирэх бүх URL (localhost, CDN) шууд ажиллана */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={bannerImageSrc(banner.banner_image)}
                alt={banner.banner_title || "Баннер"}
                className="banner-hero-img absolute inset-0 h-full w-full object-cover brightness-[0.9] contrast-[1.04] saturate-[1.03]"
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/78 via-black/[0.28] to-black/5" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/[0.82] via-black/48 to-transparent" />
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.4]"
                style={{
                  background:
                    "radial-gradient(ellipse 90% 70% at 18% 42%, rgba(87,112,153,0.2), transparent 58%), radial-gradient(ellipse 65% 55% at 100% 8%, rgba(255,255,255,0.06), transparent 45%), radial-gradient(ellipse 75% 55% at 100% 100%, rgba(0,0,0,0.5), transparent 52%)",
                }}
                aria-hidden
              />
              <div className="relative z-10 flex h-full flex-col items-start justify-center px-5 py-12 sm:px-8 md:px-14 lg:px-20">
                <div className="banner-text-panel max-w-xl rounded-3xl border border-white/12 bg-white/[0.065] p-6 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.55)] ring-1 ring-inset ring-white/10 backdrop-blur-xl sm:max-w-2xl sm:p-8 md:max-w-2xl">
                  <div
                    className="mb-5 h-1 w-14 rounded-full bg-gradient-to-r from-[#8fa8cc] to-[#577099]"
                    aria-hidden
                  />
                  <p
                    key={`${banner.id}-kicker-${isActive ? "active" : "idle"}`}
                    className="banner-desc-animated mb-3 max-w-xl text-pretty text-xs font-medium uppercase tracking-[0.12em] text-white/65 md:text-sm md:normal-case md:tracking-normal md:text-white/70"
                  >
                    {BANNER_KICKER}
                  </p>
                  <h2
                    key={`${banner.id}-${isActive ? "active" : "idle"}`}
                    className="banner-title-animated text-balance text-3xl font-semibold tracking-tight text-white [text-shadow:0_2px_28px_rgba(0,0,0,0.45)] md:text-5xl md:leading-[1.06]"
                  >
                    {banner.banner_title}
                  </h2>
                  {bannerDesc ? (
                    <p
                      key={`${banner.id}-desc-${isActive ? "active" : "idle"}`}
                      className="banner-desc-animated mt-4 max-w-xl text-pretty text-sm leading-relaxed text-white/[0.88] md:text-base"
                    >
                      {bannerDesc}
                    </p>
                  ) : null}
                  {linkAction.kind === "internal" ? (
                    <button
                      type="button"
                      key={`${banner.id}-cta-${isActive ? "active" : "idle"}`}
                      onClick={() => router.push(linkAction.path)}
                      className="banner-desc-animated mt-8 inline-flex items-center justify-center rounded-full bg-white px-7 py-3 text-sm font-semibold text-slate-900 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.35)] ring-1 ring-white/40 transition hover:bg-white/95 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                    >
                      Үргэлжлүүлэх
                    </button>
                  ) : linkAction.kind === "external" ? (
                    <button
                      type="button"
                      key={`${banner.id}-cta-${isActive ? "active" : "idle"}`}
                      onClick={() =>
                        window.open(linkAction.url, "_blank", "noopener,noreferrer")
                      }
                      className="banner-desc-animated mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-semibold text-slate-900 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.35)] ring-1 ring-white/40 transition hover:bg-white/95 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                    >
                      Дэлгэрэнгүй
                      <ExternalLink className="h-4 w-4 opacity-70" aria-hidden />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          );
          })}
        </Slider>

        {/* Доод — индикатор + сумнууд нэг хайрцагт, баруун доод буланд */}
        {safeBannersHomeBig.length > 0 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/85 via-black/35 to-transparent pt-24 pb-3">
            <div className="pointer-events-auto flex justify-end px-3 pb-3 sm:px-5 sm:pb-4 md:px-8">
              <div className="flex w-full max-w-sm items-center justify-between gap-1.5 rounded-2xl border border-white/12 bg-black/35 px-3 py-2.5 shadow-[0_12px_40px_-10px_rgba(0,0,0,0.5)] ring-1 ring-inset ring-white/10 backdrop-blur-xl sm:max-w-md">
                <div className="flex min-w-0 flex-1 items-center justify-center gap-1">
                  {safeBannersHomeBig.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => sliderRef.current?.slickGoTo(index)}
                      className="relative h-[5px] shrink-0 overflow-hidden rounded-full bg-white/20 transition-all duration-300 hover:bg-white/30"
                      style={{
                        width: index === currentSlide ? "3.4rem" : "0.9rem",
                      }}
                      aria-label={`Слайд ${index + 1}`}
                    >
                      {index === currentSlide && (
                        <span
                          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-white to-white/90 transition-[width] duration-75 ease-linear"
                          style={{ width: `${progress}%` }}
                        />
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex shrink-0 gap-0.5">
                  <button
                    type="button"
                    onClick={goToPrev}
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-white/[0.08] text-white transition hover:bg-white/[0.14] active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8fa8cc]/70"
                    aria-label="Өмнөх слайд"
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={2.25} />
                  </button>
                  <button
                    type="button"
                    onClick={goToNext}
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-white/[0.08] text-white transition hover:bg-white/[0.14] active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8fa8cc]/70"
                    aria-label="Дараагийн слайд"
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={2.25} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ✅ Хажуугийн жижиг зураг */}
      {/* <div className="w-full md:w-[25%] h-[200px] md:h-[500px] rounded-xl overflow-hidden">
        {bannersHomeSmallTop?.length > 0 && (
          <Link href={bannersHomeSmallTop[0].banner_link} target="_blank" rel="noopener noreferrer">
            <Image
              src={`${BASE_URL}${bannersHomeSmallTop[0].banner_image}`}
              alt={bannersHomeSmallTop[0].banner_title}
              width={300}
              height={400}
              className="w-full h-full object-cover rounded-md"
              priority
            />
          </Link>
        )}
      </div> */}

      <style jsx>{`
        :global(.banner-slider-root),
        :global(.banner-slider-root .slick-list),
        :global(.banner-slider-root .slick-track),
        :global(.banner-slider-root .slick-slide),
        :global(.banner-slider-root .slick-slide > div) {
          height: 100%;
        }

        .banner-skeleton-shimmer {
          position: relative;
          overflow: hidden;
        }
        .banner-skeleton-shimmer::after {
          content: "";
          position: absolute;
          inset: 0;
          transform: translateX(-100%);
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.22),
            transparent
          );
          animation: bannerShimmer 1.35s ease-in-out infinite;
        }

        @keyframes bannerShimmer {
          100% {
            transform: translateX(100%);
          }
        }

        .banner-slide--active .banner-hero-img {
          animation: heroKenBurns 14s ease-out both;
        }

        @keyframes heroKenBurns {
          0% {
            transform: scale(1) translate3d(0, 0, 0);
          }
          100% {
            transform: scale(1.06) translate3d(-0.6%, -0.4%, 0);
          }
        }

        .banner-title-animated {
          will-change: transform, opacity;
          animation-name: bannerFadeUp;
          animation-duration: 600ms;
          animation-timing-function: ease-out;
          animation-fill-mode: both;
        }

        .banner-desc-animated {
          will-change: transform, opacity;
          animation-name: bannerFadeUp;
          animation-duration: 700ms;
          animation-delay: 90ms;
          animation-timing-function: ease-out;
          animation-fill-mode: both;
        }

        @keyframes bannerFadeUp {
          0% {
            opacity: 0;
            transform: translateY(22px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .banner-title-animated,
          .banner-desc-animated {
            animation: none;
          }
          .banner-slide--active .banner-hero-img {
            animation: none;
          }
          .banner-skeleton-shimmer::after {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
